import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, testimonials, brandValues, faqData } from '@/mocks/siteInfo';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        foundingDate: `${siteInfo.foundingYear}`,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteInfo.address,
          addressCountry: 'CN',
        },
        sameAs: Object.values(siteInfo.socialLinks),
      },
      {
        '@type': 'WebPage',
        name: `${siteInfo.name} - 关于我们`,
        description: `了解${siteInfo.name}的品牌故事、核心价值观、用户口碑与联系方式，是专业的猛男蜕变指南内容平台。`,
        url: `${siteInfo.url}/about`,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、核心价值观、用户口碑与联系方式。${siteInfo.description}`}
        keywords="猛男诞生记,关于我们,品牌介绍,健身社区,用户口碑,联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
        schema={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="max-w-3xl mt-4">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 tracking-wide">
                关于猛男诞生记
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
                {siteInfo.name}创立于{siteInfo.foundingYear}年，是国内领先的男性健身蜕变内容平台。
                我们致力于为每一位追求更好自己的男性提供科学、真实、可执行的训练方案和生活方式指南。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  我们的故事
                </h2>
                <div className="mt-6 space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                  <p>
                    猛男诞生记的创始团队由一群热爱健身的运动科学专家、营养师和内容创作者组成。
                    我们曾经也是从零开始的健身小白，经历过迷茫、碰壁、受伤，走过无数弯路。
                    正是因为亲身体验过'没有人告诉你该怎么做'的困惑，我们才决定做这件事——
                    打造一个真正能帮到普通人的健身蜕变指南。
                  </p>
                  <p>
                    六年多来，我们坚持原创内容，拒绝伪科学和速成神话。
                    每一篇文章都经过运动科学和营养学专业审核，每一个训练计划都经过团队成员亲自验证。
                    我们相信，真正的猛男不在于肌肉有多大，而在于能否掌控自己的身体和人生。
                  </p>
                  <p>
                    到今天，猛男诞生记已经拥有了超过128万忠实读者，累计发布了586篇原创文章，
                    帮助数万人完成了从'健身小白'到'真正猛男'的蜕变。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-72 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Fitness%20community%20team%20of%20professional%20coaches%20and%20athletes%20in%20modern%20gym%2C%20group%20photo%2C%20warm%20atmosphere%2C%20natural%20lighting%2C%20professional%20fitness%20photography%2C%20casual%20confident%20poses&width=800&height=600&seq=about-story-image&orientation=landscape"
                  alt="猛男诞生记团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                核心价值观
              </h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                指导我们每一天工作的四个信念
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandValues.map((value) => (
                <div
                  key={value.title}
                  className="bg-background-50 rounded-lg p-5 md:p-6"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${value.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-900">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                猛男口碑
              </h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                真实的蜕变故事，来自真实的猛男们
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100 rounded-lg p-5 md:p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.age}岁 · {t.duration}蜕变</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <div className="p-3 rounded-md bg-accent-50 border border-accent-100/60">
                      <p className="text-xs text-foreground-400 mb-1">蜕变前</p>
                      <p>{t.before}</p>
                    </div>
                    <div className="p-3 rounded-md bg-primary-50 border border-primary-100/60">
                      <p className="text-xs text-foreground-400 mb-1">蜕变后</p>
                      <p>{t.after}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                常见问题
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-foreground-900 font-medium text-sm md:text-base hover:text-primary-600 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform flex-shrink-0 ml-3"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-3 text-foreground-500 text-sm md:text-base">
                  有任何问题或合作意向？欢迎通过以下方式联系我们
                </p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">邮箱</div>
                      <div className="text-sm text-foreground-500">{siteInfo.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电话</div>
                      <div className="text-sm text-foreground-500">{siteInfo.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">微信</div>
                      <div className="text-sm text-foreground-500">{siteInfo.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">地址</div>
                      <div className="text-sm text-foreground-500">{siteInfo.address}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  data-readdy-form="true"
                  action="https://readdy.ai/api/form/d9s9beee3oq4jqumu7g0"
                  method="POST"
                  className="bg-background-100 rounded-lg p-5 md:p-6"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                    if (honeypot) {
                      const feedback = form.querySelector('.form-feedback') as HTMLElement;
                      if (feedback) {
                        feedback.textContent = '提交成功，我们会尽快回复！';
                        feedback.className = 'form-feedback text-sm text-primary-600 mt-3';
                      }
                      form.reset();
                      return;
                    }
                    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                    if (submitBtn) submitBtn.disabled = true;
                    const feedback = form.querySelector('.form-feedback') as HTMLElement;
                    try {
                      const formData = new FormData(form);
                      const params = new URLSearchParams();
                      formData.forEach((val, key) => {
                        if (key !== 'phone_alt') params.append(key, val as string);
                      });
                      const res = await fetch(form.action, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: params.toString(),
                      });
                      const resText = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(resText); } catch { parsed = null; }
                      if (res.ok && parsed?.code === 'OK') {
                        if (feedback) {
                          feedback.textContent = '提交成功，我们会尽快回复！';
                          feedback.className = 'form-feedback text-sm text-primary-600 mt-3';
                        }
                        form.reset();
                      } else {
                        const msg = parsed?.meta?.message || parsed?.message || resText || '提交失败，请稍后再试';
                        if (feedback) {
                          feedback.textContent = msg;
                          feedback.className = 'form-feedback text-sm text-accent-600 mt-3';
                        }
                      }
                    } catch {
                      if (feedback) {
                        feedback.textContent = '网络错误，请检查网络后重试';
                        feedback.className = 'form-feedback text-sm text-accent-600 mt-3';
                      }
                    }
                    if (submitBtn) submitBtn.disabled = false;
                  }}
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-md transition-colors cursor-pointer text-sm whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </div>
                  <div className="form-feedback text-sm mt-3"></div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}